import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { AlertTriangle, Loader2, RefreshCw, Users } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { JudgeOptionLabel } from "./JudgeOptionLabel";
import { runReassignment } from "./actDownloads";
import { DateInput } from "./ui/date-input";

const normName = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, " ").trim().toLowerCase();
const fmt = (d) => (d || "").split("-").reverse().join("/");
const today = () => new Date().toISOString().slice(0, 10);

// «ΑΝΑΧΡΕΩΣΗ» δικασίμου: αντικατάσταση Δικαστή Προδικαστικού + Έδρας σε όλες/επιλεγμένες υποθέσεις
// + ΠΡΑΞΗ ΝΕΟΥ ΟΡΙΣΜΟΥ ΔΙΚΑΣΤΗ/ΕΙΣΗΓΗΤΗ ανά υπόθεση (ZIP).
// target: { pinakioLabel, hearingDate, courtType, caseIds?: [id] (μόνο αυτές), presetNewJudgeName?: (προεπιλογή νέου δικαστή, π.χ. Δικαστής Έδρας) }
export const ReassignDialog = ({ target, onClose, onDone }) => {
  const [info, setInfo] = useState(null);
  const [judges, setJudges] = useState([]);
  const [oldJudge, setOldJudge] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [newJudgeId, setNewJudgeId] = useState("");
  const [reason, setReason] = useState("court");
  const [customReason, setCustomReason] = useState("");
  const [startNum, setStartNum] = useState("");
  const [actYear, setActYear] = useState(String(new Date().getFullYear()));
  const [issueDate, setIssueDate] = useState(today());
  const [format, setFormat] = useState("docx");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const ct = info?.court_type || target?.courtType || "Μονομελές";
  const isPoly = ct === "Πολυμελές";
  const onlyIds = useMemo(() => new Set(target?.caseIds || []), [target]);
  const singleCase = onlyIds.size > 0;

  useEffect(() => {
    if (!target) return;
    setInfo(null); setOldJudge(""); setSelected(new Set()); setNewJudgeId(""); setReason("court"); setCustomReason("");
    setIssueDate(today()); setActYear(String(new Date().getFullYear())); setLoading(true);
    const params = new URLSearchParams({ pinakio_label: target.pinakioLabel, hearing_date: target.hearingDate });
    if (target.courtType) params.append("court_type", target.courtType);
    Promise.all([
      axios.get(`${API}/reassignment/hearing?${params.toString()}`),
      axios.get(`${API}/reassignment/next-act-number?year=${new Date().getFullYear()}`),
    ]).then(([h, n]) => {
      setInfo(h.data);
      const wanted = new Set(target.caseIds || []);
      const focus = wanted.size ? (h.data.cases || []).find(c => wanted.has(c.id) && c.assigned_judge) : null;
      const js = h.data.judges || [];
      setOldJudge(focus?.assigned_judge || (js.length === 1 ? js[0] : (js[0] || "")));
      setStartNum(String(n.data?.next || 1));
      return axios.get(`${API}/pretrial-judges-list?court_type=${encodeURIComponent(h.data.court_type || target.courtType || "Μονομελές")}`);
    }).then(r => {
      const list = r?.data?.judges || [];
      setJudges(list);
      if (target.presetNewJudgeName) {
        const hit = list.find(j => normName(j.full_name) === normName(target.presetNewJudgeName));
        if (hit) setNewJudgeId(hit.id);
      }
    })
      .catch(e => toast.error(e.response?.data?.detail || "Σφάλμα φόρτωσης δικασίμου"))
      .finally(() => setLoading(false));
  }, [target]);

  const oldCases = useMemo(() => (info?.cases || []).filter(c => c.assigned_judge && (!onlyIds.size || onlyIds.has(c.id))
    && (!oldJudge || normName(c.assigned_judge) === normName(oldJudge))), [info, oldJudge, onlyIds]);
  useEffect(() => { setSelected(new Set(oldCases.map(c => c.id))); }, [oldCases]);

  const members = useMemo(() => {
    if ((info?.composition_members || []).length) return info.composition_members;
    const src = (info?.cases || []).find(c => c.composition_members?.length);
    return (src?.composition_members || []).filter(m => !(m && typeof m === "object" && m.excluded))
      .map(m => (typeof m === "string" ? m : m?.name)).filter(Boolean);
  }, [info]);
  const excludedFromSelected = useMemo(() => {
    const s = new Set();
    (info?.cases || []).filter(c => selected.has(c.id)).forEach(c => (c.excluded_judges || []).forEach(n => s.add(normName(n))));
    return s;
  }, [info, selected]);
  const memberGrades = useMemo(() => {
    const g = {};
    (info?.cases || []).forEach(c => (c.composition_members || []).forEach(m => {
      if (m && typeof m === "object" && m.name) g[normName(m.name)] = m.grade || "";
    }));
    return g;
  }, [info]);
  const candidates = useMemo(() => {
    const list = judges.filter(j => normName(j.full_name) !== normName(oldJudge) && !excludedFromSelected.has(normName(j.full_name)));
    if (!isPoly || !members.length) return list;
    // iter94: ΟΛΑ τα μέλη της Σύνθεσης (τακτικά + αναπληρωματικά) — ακόμη κι αν δεν είναι στο μητρώο δικαστών
    return members.filter(n => normName(n) !== normName(oldJudge) && !excludedFromSelected.has(normName(n))).map(n => {
      const hit = list.find(j => normName(j.full_name) === normName(n));
      return hit || { id: `name:${n}`, full_name: n, grade: memberGrades[normName(n)] || "" };
    });
  }, [judges, oldJudge, isPoly, members, excludedFromSelected, memberGrades]);
  const newJudge = candidates.find(j => j.id === newJudgeId) || judges.find(j => j.id === newJudgeId);
  const toggle = (id) => setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const canSubmit = !busy && newJudgeId && selected.size > 0 && parseInt(startNum, 10) >= 1 && (reason !== "custom" || customReason.trim());

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const res = await runReassignment({
        pinakio_label: target.pinakioLabel, hearing_date: target.hearingDate, court_type: ct,
        case_ids: [...selected], old_judge_name: oldJudge || null,
        new_judge_id: newJudgeId.startsWith("name:") ? null : newJudgeId,
        new_judge_name: newJudgeId.startsWith("name:") ? newJudgeId.slice(5) : (newJudge?.full_name || null),
        reason, custom_reason: customReason, starting_act_number: parseInt(startNum, 10), act_year: actYear,
        issue_date: issueDate, format,
      });
      toast.success(`ΑΝΑΧΡΕΩΣΗ ολοκληρώθηκε: ${res.generated} πράξεις (${res.firstAct}–${res.lastAct}/${actYear}) → ${newJudge?.full_name}`, { duration: 8000 });
      onDone?.(); onClose();
    } catch (e) {
      toast.error(e.message, { duration: 15000 });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="reassign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><RefreshCw className="w-5 h-5 text-orange-600" />ΑΝΑΧΡΕΩΣΗ — Νέος Ορισμός {isPoly ? "Εισηγητή" : "Δικαστή"}</DialogTitle>
          <DialogDescription>
            {target?.pinakioLabel} — {fmt(target?.hearingDate)} · {ct}
            {info?.composition_label ? <span className="ml-1 text-violet-800"><Users className="inline w-3 h-3" /> Σύνθεση «{info.composition_label}»</span> : null}
            {singleCase && <span className="ml-1 font-semibold text-red-700" data-testid="reassign-single-case-hint">· μόνο η επιλεγμένη υπόθεση</span>}
            <br /><span className="text-xs">Αντικαθιστά τον Δικαστή Προδικαστικού <b>και</b> τον Δικαστή Έδρας και εκδίδει ΠΡΑΞΗ ΝΕΟΥ ΟΡΙΣΜΟΥ ανά υπόθεση (ZIP).
            {isPoly && members.length ? " Νέος Εισηγητής: μόνο μέλος της Σύνθεσης που εκδικάζει την υπόθεση." : ""}</span>
          </DialogDescription>
        </DialogHeader>
        {loading ? <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : info && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Αντικαθίσταται ο/η</Label>
                <Select value={oldJudge} onValueChange={setOldJudge}>
                  <SelectTrigger className="h-9" data-testid="reassign-old-judge"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{(info.judges || []).map(j => <SelectItem key={j} value={j}>{j}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Νέος {isPoly ? "Εισηγητής" : "Δικαστής"} {isPoly && members.length ? "(μέλος της σύνθεσης)" : ""}</Label>
                <Select value={newJudgeId} onValueChange={setNewJudgeId}>
                  <SelectTrigger className="h-9" data-testid="reassign-new-judge"><SelectValue placeholder="Επιλέξτε δικαστή…" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {candidates.map(j => <SelectItem key={j.id} value={j.id} data-testid={`reassign-judge-option-${j.id}`}><JudgeOptionLabel judge={j} /></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="rounded border bg-slate-50 p-2">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="font-semibold">Υποθέσεις προς ΑΝΑΧΡΕΩΣΗ: {selected.size}/{oldCases.length}</span>
                <button type="button" className="text-blue-700 hover:underline" onClick={() => setSelected(new Set(selected.size === oldCases.length ? [] : oldCases.map(c => c.id)))} data-testid="reassign-toggle-all">
                  {selected.size === oldCases.length ? "Καμία" : "Όλες"}
                </button>
              </div>
              <div className="max-h-36 overflow-y-auto divide-y" data-testid="reassign-case-list">
                {oldCases.map(c => (
                  <label key={c.id} className="flex items-center gap-2 py-1 text-xs cursor-pointer">
                    <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggle(c.id)} data-testid={`reassign-case-${c.id}`} />
                    <span className="font-mono font-semibold w-24">{c.gak}</span>
                    <span className="font-mono text-slate-500 w-16">{c.eak || "—"}</span>
                    <span className="truncate flex-1 text-slate-700">{c.subject}</span>
                    {c.assignment_act_number && <span className="text-emerald-700 font-mono">Πρ. {c.assignment_act_number}/{c.assignment_act_year}</span>}
                    {c.reassignment_act_number && <span className="text-orange-700 font-mono" title={`Προηγούμενη ΑΝΑΧΡΕΩΣΗ από ${c.reassigned_from}`}>ΑΝΑΧΡ. {c.reassignment_act_number}/{c.reassignment_act_year}</span>}
                    {c.needs_reassignment && <span className="text-red-700 font-bold" title={c.needs_reassignment.reason} data-testid={`reassign-case-needs-${c.id}`}>ΑΠΑΙΤΕΙΤΑΙ</span>}
                  </label>
                ))}
                {!oldCases.length && <p className="text-xs text-slate-500 italic py-1">Καμία υπόθεση με ορισμένο δικαστή.</p>}
              </div>
            </div>

            <div>
              <Label className="text-xs">Λόγος (παρ. Β της Πράξης)</Label>
              <RadioGroup value={reason} onValueChange={setReason} className="mt-1 space-y-1">
                <label className="flex items-center gap-2 text-xs"><RadioGroupItem value="court" data-testid="reassign-reason-court" />δεν υπηρετεί πλέον στο Πρωτοδικείο Αθηνών</label>
                <label className="flex items-center gap-2 text-xs"><RadioGroupItem value="sector" data-testid="reassign-reason-sector" />δεν υπηρετεί πλέον στο Τμήμα / στην Περιφερειακή Έδρα (συμπληρώνεται αυτόματα ανά υπόθεση)</label>
                <label className="flex items-center gap-2 text-xs"><RadioGroupItem value="custom" data-testid="reassign-reason-custom" />άλλος λόγος:</label>
              </RadioGroup>
              {reason === "custom" && <Input className="h-8 mt-1 text-xs" value={customReason} onChange={e => setCustomReason(e.target.value)} placeholder="π.χ. τελεί σε μακροχρόνια άδεια" data-testid="reassign-reason-text" />}
            </div>

            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Αρ. 1ης Πράξης</Label>
                <Input type="number" min="1" className="h-8" value={startNum} onChange={e => setStartNum(e.target.value)} data-testid="reassign-start-number" />
              </div>
              <div>
                <Label className="text-xs">Έτος</Label>
                <Input className="h-8" value={actYear} onChange={e => setActYear(e.target.value)} data-testid="reassign-year" />
              </div>
              <div>
                <Label className="text-xs">Ημ. έκδοσης</Label>
                <DateInput className="h-8" value={issueDate} onChange={e => setIssueDate(e.target.value)} data-testid="reassign-issue-date" />
              </div>
              <div>
                <Label className="text-xs">Μορφή</Label>
                <Select value={format} onValueChange={setFormat}>
                  <SelectTrigger className="h-8" data-testid="reassign-format"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="docx">Word</SelectItem><SelectItem value="pdf">PDF</SelectItem><SelectItem value="odt">ODT</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-[11px] text-slate-500">Οι πράξεις αριθμούνται αυξητικά από τον αρ. {startNum || "…"} (ξεχωριστή αρίθμηση Πράξεων ΑΝΑΧΡΕΩΣΗΣ). Ελέγχονται οι κανόνες σύνθεσης, «ένας τύπος δικαστηρίου ανά ημέρα» και το όριο υποθέσεων ανά δικαστή.</p>
            {newJudge && newJudge.case_count != null && newJudge.case_count + selected.size > newJudge.case_limit && (
              <p className="text-xs text-red-700 flex items-center gap-1" data-testid="reassign-limit-warning"><AlertTriangle className="w-3.5 h-3.5" />Ο/Η {newJudge.full_name} έχει {newJudge.case_count}/{newJudge.case_limit} — η ΑΝΑΧΡΕΩΣΗ {selected.size} υποθέσεων θα υπερβεί το όριο.</p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="reassign-cancel">Άκυρο</Button>
          <Button className="bg-orange-600 hover:bg-orange-700" onClick={submit} disabled={!canSubmit} data-testid="reassign-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1" />ΑΝΑΧΡΕΩΣΗ + Πράξεις (ZIP)</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReassignDialog;
